export const COLORS = {
  sessions: "#6366F1",
  selfService: "#14B8A6",
  videoCalls: "#F59E0B",
  completed: "#16A34A",
  cash: "#0EA5E9",
  card: "#8B5CF6",
  cancelled: "#EF4444",
  noFare: "#64748B",
  muted: "#A8A8B3",
};

/** KPI order + icon metadata. `key` matches the API `kpis` object. */
const KPI_DEFS = [
  { key: "sessions", title: "Booking Sessions Started", icon: "MonitorIcon", iconColor: COLORS.sessions },
  { key: "selfServiceBookings", title: "Self Service Bookings", icon: "MousePointerIcon", iconColor: COLORS.selfService },
  { key: "videoCalls", title: "Video Call Clicks", icon: "VideoIcon", iconColor: COLORS.videoCalls },
  { key: "completed", title: "Completed Bookings", icon: "CheckCircleIcon", iconColor: COLORS.completed },
  // Optional: shown only when the API sends the field.
  { key: "cancelled", title: "Cancelled Bookings", icon: "XCircleIcon", iconColor: COLORS.cancelled, optional: true },
  { key: "noFare", title: "No Fare Bookings", icon: "SlashIcon", iconColor: COLORS.noFare, optional: true },
  { key: "activeKiosks", title: "Active Kiosks", icon: "WifiIcon", iconColor: COLORS.sessions },
  { key: "revenue", title: "Revenue", icon: "TrendingUpIcon", iconColor: "#fff", highlight: true },
];

const num = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

export const pct = (part, whole) =>
  whole > 0 ? Math.round((part / whole) * 1000) / 10 : 0;

export function formatNumber(value) {
  return num(value).toLocaleString("en-GB");
}

export function formatCurrency(value) {
  if (typeof value === "string" && value.trim() !== "" && Number.isNaN(Number(value))) {
    return value;
  }
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num(value));
}

export function toIsoDate(date) {
  if (!date) return "";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function getDefaultDateRange() {
  const to = new Date();
  to.setHours(0, 0, 0, 0);
  const from = new Date(to);
  from.setDate(from.getDate() - 6);
  return { from, to };
}

export function emptyKioskAnalytics() {
  return {
    totals: {
      sessions: 0,
      selfServiceBookings: 0,
      videoCalls: 0,
      completed: 0,
      activeKiosks: 0,
      totalKiosks: 0,
      revenue: 0,
      cashBookings: 0,
      cardBookings: 0,
      cashRevenue: 0,
      cardRevenue: 0,
      cancelled: 0,
      noFare: 0,
    },
    hasPaymentSplit: false,
    hasCancelled: false,
    hasNoFare: false,
    kpis: [],
    daily: null,
    kiosks: null,
  };
}

/**
 * Live API returns `metrics` (numbers) plus a `kpis` array (display titles/values):
 *   metrics: { bookingSessionsStarted, selfServiceBookings, videoCallClicks,
 *              completedBookings, activeKiosks, revenue }
 * Map those names onto the screen's internal keys.
 */
const METRIC_ALIASES = {
  sessions: ["sessions", "bookingSessionsStarted"],
  selfServiceBookings: ["selfServiceBookings"],
  videoCalls: ["videoCalls", "videoCallClicks"],
  completed: ["completed", "completedBookings"],
  activeKiosks: ["activeKiosks"],
  totalKiosks: ["totalKiosks", "installedKiosks"],
  revenue: ["revenue"],
  cancelled: ["cancelledBookings", "cancelled_bookings", "canceledBookings"],
  noFare: ["noFareBookings", "no_fare_bookings", "nofareBookings"],
  cashBookings: ["cashBookings", "cash_bookings"],
  cardBookings: ["cardBookings", "card_bookings"],
  cashRevenue: ["cashRevenue", "cash_revenue"],
  cardRevenue: ["cardRevenue", "card_revenue"],
};

const PAYMENT_KEYS = ["cashBookings", "cardBookings", "cashRevenue", "cardRevenue"];
const PAYMENT_ALIASES = {
  cashBookings: METRIC_ALIASES.cashBookings,
  cardBookings: METRIC_ALIASES.cardBookings,
  cashRevenue: METRIC_ALIASES.cashRevenue,
  cardRevenue: METRIC_ALIASES.cardRevenue,
};

/** Reads cash/card fields from an object; `present` is false if the API sent none of them. */
function readPaymentSplit(obj) {
  const out = { present: false };
  PAYMENT_KEYS.forEach((key) => {
    const alias = PAYMENT_ALIASES[key].find((n) => obj && obj[n] != null);
    out[key] = alias ? num(obj[alias]) : 0;
    if (alias) out.present = true;
  });
  return out;
}

/** Titles the API uses in its `kpis` array → internal key (fallback when `metrics` is absent). */
const KPI_TITLE_KEYS = {
  "booking sessions started": "sessions",
  "self service bookings": "selfServiceBookings",
  "video call clicks": "videoCalls",
  "completed bookings": "completed",
  "cancelled bookings": "cancelled",
  "no fare bookings": "noFare",
  "active kiosks": "activeKiosks",
  "revenue": "revenue",
};

/** "1,234" / "0.00 £" / "£12.50" → number */
const parseLooseNumber = (value) => {
  if (typeof value === "number") return value;
  const cleaned = String(value == null ? "" : value).replace(/[^0-9.-]/g, "");
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
};

function readRawMetrics(payload) {
  const out = {};
  const sources = [payload.metrics, Array.isArray(payload.kpis) ? null : payload.kpis]
    .filter((src) => src && typeof src === "object");

  Object.keys(METRIC_ALIASES).forEach((key) => {
    for (const src of sources) {
      const alias = METRIC_ALIASES[key].find((name) => src[name] != null);
      if (alias) {
        out[key] = num(src[alias]);
        return;
      }
    }
  });

  if (Array.isArray(payload.kpis)) {
    payload.kpis.forEach((kpi) => {
      const key = KPI_TITLE_KEYS[String(kpi.title || "").trim().toLowerCase()];
      if (key && out[key] == null) out[key] = parseLooseNumber(kpi.value);
    });
  }
  return out;
}

const unwrap = (response) => {
  const root = response && response.data !== undefined ? response.data : response;
  if (!root || typeof root !== "object") return {};
  if (root.data && typeof root.data === "object" && !Array.isArray(root.data)) {
    return root.data;
  }
  return root;
};

/** Normalise an API response (or mock payload) into what the screen renders. */
export function normalizeKioskAnalytics(response) {
  const payload = unwrap(response);
  const rawKpis = readRawMetrics(payload);

  // Accepts both the live API names (bookingSessionsStarted, videoCallClicks,
  // completedBookings, venueName, lastSeenAt, deviceId) and the original contract names.
  const first = (obj, names) => {
    const hit = names.find((n) => obj[n] != null);
    return hit ? obj[hit] : undefined;
  };
  const kiosks = (payload.kiosks || []).map((k, index) => {
    const sessions = num(first(k, ["sessions", "bookingSessionsStarted"]));
    const selfServiceBookings = num(k.selfServiceBookings);
    const videoCalls = num(first(k, ["videoCalls", "videoCallClicks"]));
    const completed = num(first(k, ["completed", "completedBookings"]));
    const id = k.id != null ? k.id : `kiosk-${index}`;
    const activeFlag = first(k, ["isActive", "active"]);
    const pay = readPaymentSplit(k);
    const cancelledRaw = first(k, METRIC_ALIASES.cancelled);
    const noFareRaw = first(k, METRIC_ALIASES.noFare);
    return {
      id,
      name: k.name || (k.id != null ? `Kiosk #${k.id}` : `Kiosk ${index + 1}`),
      deviceId: k.deviceId || k.device_id || "",
      venue: first(k, ["venueName", "venue"]) || "—",
      venueId: first(k, ["venueId", "venue_id"]) || null,
      company: k.company || "",
      sessions,
      selfServiceBookings,
      videoCalls,
      completed,
      revenue: num(k.revenue),
      revenueFormatted: k.revenueFormatted || "",
      conversion: pct(completed, sessions),
      lastActiveAt: first(k, ["lastActiveAt", "lastSeenAt"]) || null,
      // null = the API didn't say; the table then shows "last seen" only.
      isActive: activeFlag === undefined ? null : !!activeFlag,
      hasPaymentSplit: pay.present,
      cashBookings: pay.cashBookings,
      cardBookings: pay.cardBookings,
      cashRevenue: pay.cashRevenue,
      cardRevenue: pay.cardRevenue,
      cancelled: num(cancelledRaw),
      noFare: num(noFareRaw),
      hasCancelled: cancelledRaw !== undefined,
      hasNoFare: noFareRaw !== undefined,
    };
  });

  const sumKiosks = (field) => kiosks.reduce((acc, k) => acc + k[field], 0);
  const pick = (field, fallback) =>
    rawKpis[field] != null ? num(rawKpis[field]) : fallback;

  const totals = {
    sessions: pick("sessions", sumKiosks("sessions")),
    selfServiceBookings: pick("selfServiceBookings", sumKiosks("selfServiceBookings")),
    videoCalls: pick("videoCalls", sumKiosks("videoCalls")),
    completed: pick("completed", sumKiosks("completed")),
    activeKiosks: pick("activeKiosks", kiosks.filter((k) => k.isActive === true).length),
    totalKiosks: pick("totalKiosks", kiosks.length),
    revenue: pick("revenue", sumKiosks("revenue")),
    cashBookings: pick("cashBookings", sumKiosks("cashBookings")),
    cardBookings: pick("cardBookings", sumKiosks("cardBookings")),
    cashRevenue: pick("cashRevenue", sumKiosks("cashRevenue")),
    cardRevenue: pick("cardRevenue", sumKiosks("cardRevenue")),
    cancelled: pick("cancelled", sumKiosks("cancelled")),
    noFare: pick("noFare", sumKiosks("noFare")),
  };
  const present = {
    cancelled: rawKpis.cancelled != null || kiosks.some((k) => k.hasCancelled),
    noFare: rawKpis.noFare != null || kiosks.some((k) => k.hasNoFare),
  };
  // Show the cash/card sections only once the API sends at least one of those fields.
  const hasPaymentSplit =
    PAYMENT_KEYS.some((key) => rawKpis[key] != null) ||
    kiosks.some((k) => k.hasPaymentSplit);

  const kpis = KPI_DEFS.filter((def) => !def.optional || present[def.key]).map((def) => {
    let value;
    let caption = "";
    if (def.key === "revenue") {
      value = formatCurrency(totals.revenue);
      caption = totals.completed
        ? `${formatCurrency(totals.revenue / totals.completed)} avg / trip`
        : "";
    } else if (def.key === "activeKiosks") {
      value = `${formatNumber(totals.activeKiosks)}`;
      caption = totals.totalKiosks ? `of ${formatNumber(totals.totalKiosks)} installed` : "";
    } else {
      value = formatNumber(totals[def.key]);
      if (def.key !== "sessions") {
        caption = `${pct(totals[def.key], totals.sessions)}% of sessions`;
      }
    }
    return { ...def, value, caption, highlight: !!def.highlight };
  });

  // `daily` / `kiosks` are optional: null = not provided by the API (section hidden).
  const daily = Array.isArray(payload.daily) ? payload.daily.map((d) => ({
    date: d.date,
    sessions: num(d.sessions),
    selfServiceBookings: num(d.selfServiceBookings),
    videoCalls: num(d.videoCalls),
    completed: num(d.completed),
  })) : null;

  return {
    totals,
    hasPaymentSplit,
    hasCancelled: present.cancelled,
    hasNoFare: present.noFare,
    kpis,
    daily,
    kiosks: Array.isArray(payload.kiosks) ? kiosks : null,
  };
}

/* ------------------------------------------------------------------ */
/* Sample data — used only until POST admin/kiosk-analytics is live.   */
/* Deterministic per date range so the screen doesn't jump on reload.  */
/* ------------------------------------------------------------------ */

const SAMPLE_KIOSKS = [
  { name: "Arrivals Hall A", venue: "Birmingham Airport", company: "TaxiPoint" },
  { name: "Arrivals Hall B", venue: "Birmingham Airport", company: "TaxiPoint" },
  { name: "Main Concourse", venue: "Coalville Station", company: "Coalville Cars" },
  { name: "Reception", venue: "Hilton Leicester", company: "Coalville Cars" },
  { name: "Ground Floor", venue: "Highcross Shopping", company: "TaxiPoint" },
  { name: "Lobby", venue: "Royal Infirmary", company: "Infocab" },
  { name: "Exit Gate", venue: "NEC Hall 5", company: "TaxiPoint" },
];

function seededRandom(seed) {
  let s = seed % 2147483647;
  if (s <= 0) s += 2147483646;
  return () => {
    s = (s * 16807) % 2147483647;
    return (s - 1) / 2147483646;
  };
}

export function buildSampleKioskAnalytics(dateFrom, dateTo) {
  const from = new Date(dateFrom);
  const to = new Date(dateTo);
  from.setHours(0, 0, 0, 0);
  to.setHours(0, 0, 0, 0);
  const days = Math.max(1, Math.round((to - from) / 86400000) + 1);
  const rand = seededRandom(Number(toIsoDate(from).replace(/-/g, "")) + days);

  const daily = [];
  for (let i = 0; i < days; i += 1) {
    const d = new Date(from);
    d.setDate(from.getDate() + i);
    const weekend = d.getDay() === 0 || d.getDay() === 6;
    const sessions = Math.round((weekend ? 260 : 190) + rand() * 90);
    const selfServiceBookings = Math.round(sessions * (0.42 + rand() * 0.1));
    const videoCalls = Math.round(sessions * (0.14 + rand() * 0.06));
    const completed = Math.round((selfServiceBookings + videoCalls) * (0.82 + rand() * 0.1));
    daily.push({ date: toIsoDate(d), sessions, selfServiceBookings, videoCalls, completed });
  }

  const totalSessions = daily.reduce((a, d) => a + d.sessions, 0);
  const weights = SAMPLE_KIOSKS.map(() => 0.4 + rand());
  const weightSum = weights.reduce((a, w) => a + w, 0);
  const now = Date.now();

  const kiosks = SAMPLE_KIOSKS.map((k, i) => {
    const sessions = Math.round((totalSessions * weights[i]) / weightSum);
    const selfServiceBookings = Math.round(sessions * (0.38 + rand() * 0.16));
    const videoCalls = Math.round(sessions * (0.12 + rand() * 0.1));
    const completed = Math.round((selfServiceBookings + videoCalls) * (0.78 + rand() * 0.14));
    const isActive = i !== 5;
    const minutesAgo = isActive ? Math.round(rand() * 12) : 60 * 26 + Math.round(rand() * 300);
    return {
      id: i + 1,
      ...k,
      sessions,
      selfServiceBookings,
      videoCalls,
      completed,
      revenue: Math.round(completed * (14 + rand() * 9)),
      lastActiveAt: new Date(now - minutesAgo * 60000).toISOString(),
      isActive,
    };
  });

  return {
    kpis: {
      sessions: kiosks.reduce((a, k) => a + k.sessions, 0),
      selfServiceBookings: kiosks.reduce((a, k) => a + k.selfServiceBookings, 0),
      videoCalls: kiosks.reduce((a, k) => a + k.videoCalls, 0),
      completed: kiosks.reduce((a, k) => a + k.completed, 0),
      activeKiosks: kiosks.filter((k) => k.isActive).length,
      totalKiosks: kiosks.length,
      revenue: kiosks.reduce((a, k) => a + k.revenue, 0),
    },
    daily,
    kiosks,
  };
}
